import { normalizeDomain } from '@inboundx/shared/platform';
import { useEffect, useMemo, useState } from 'react';

import { useWorkspace } from '@/contexts/WorkspaceContext';
import { fetchDomainAliasGroup } from '@/services/domainAliases';

export interface AliasDomains {
    /** Alias domains only (excludes the selected canonical). Config is shared with these. */
    aliases: string[];
    /**
     * Full alias group, canonical first: `[selectedDomain, ...aliases]`. Empty when no
     * aliases. These share the selected domain's config, so it's safe for "settings apply
     * to all these sites" copy.
     */
    group: string[];
    /**
     * Every visible workspace whose stats merge into this view, canonical first. Superset
     * of `group`: aliases (rolled up server-side, IX-3816) PLUS other picker-visible
     * workspaces whose domains share the same root — stats RPCs aggregate cross-subdomain
     * by root (e.g. `matera.eu` + `get.matera.eu`; IX-3942). Config is NOT shared for the
     * root-sibling case, so use this ONLY for stats/traffic copy, never for "settings apply
     * to all". Empty when the view combines only one workspace.
     */
    statsGroup: string[];
}

/**
 * Alias domains that roll up into the currently selected (canonical) domain.
 *
 * Aliases are hidden from the picker but share the canonical's config and have
 * their traffic aggregated into it (IX-3816). Surfacing them lets a client see
 * their other regional websites are included, not missing (IX-3942). Both lists are
 * empty when the selected domain has no aliases.
 */
export function useAliasDomains(): AliasDomains {
    const { selectedDomain, workspaces } = useWorkspace();
    const [aliases, setAliases] = useState<string[]>([]);

    useEffect(() => {
        if (!selectedDomain) {
            setAliases([]);
            return;
        }
        let active = true;
        fetchDomainAliasGroup(selectedDomain)
            .then((groupRows) => {
                if (active) {
                    setAliases(groupRows.filter((d) => d !== selectedDomain));
                }
            })
            .catch(() => {
                if (active) {
                    setAliases([]);
                }
            });
        return () => {
            active = false;
        };
    }, [selectedDomain]);

    // Other picker-visible workspaces whose domains share this domain's root (e.g.
    // matera.eu + get.matera.eu). Stats RPCs aggregate cross-subdomain by root, so their
    // numbers fold into this view even though they have separate configuration.
    const rootSiblings = useMemo(() => {
        if (!selectedDomain) {
            return [];
        }
        const selectedRoot = normalizeDomain(selectedDomain);
        return workspaces
            .map((workspace) => workspace.domain)
            .filter((d) => d !== selectedDomain && normalizeDomain(d) === selectedRoot);
    }, [workspaces, selectedDomain]);

    const group = aliases.length > 0 ? [selectedDomain, ...aliases] : [];

    // Union of alias + root-sibling domains, deduped. Both merge into this view's stats.
    const statsSiblings = [...new Set([...aliases, ...rootSiblings])];

    return {
        aliases,
        group,
        statsGroup: statsSiblings.length > 0 ? [selectedDomain, ...statsSiblings] : [],
    };
}
